import DOMPurify from 'dompurify';

export const GMAIL_PASTE_HINT = 'Select this text and press Ctrl/Cmd+V to paste your email.';

const HTML_TAG_RE = /<\/?[a-z][\s\S]*?>/i;
const DOCUMENT_RE = /<(html|head|body|style)[\s>]/i;

const PURIFY_BASE = {
  ADD_TAGS: ['style'],
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'option', 'link', 'meta', 'base'],
};

export function looksLikeHtml(text) {
  return HTML_TAG_RE.test(String(text || ''));
}

export function plainTextToHtml(text) {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p style="font-family:Roboto,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1B4C87;">${escaped.replace(/\n/g, '<br />')}</p>`;
}

/** Full email documents keep <head>/<style>; fragments stay fragments. */
export function sanitizeEmailHtml(html) {
  const source = String(html || '');
  if (!source.trim()) return '';
  const config = DOCUMENT_RE.test(source)
    ? { ...PURIFY_BASE, WHOLE_DOCUMENT: true }
    : { ...PURIFY_BASE, FORCE_BODY: true };
  return DOMPurify.sanitize(source, config);
}

/** Write/HTML editor value → sanitized HTML (plain text is converted, not interpreted). */
export function composeBodyToHtml(body) {
  const value = String(body || '');
  if (!value.trim()) return '';
  return sanitizeEmailHtml(looksLikeHtml(value) ? value : plainTextToHtml(value));
}

/**
 * Appends the open-tracking pixel to already-sanitized HTML (inside <body> for
 * full documents). Not display:none — some clients skip hidden images.
 */
export function appendTrackingPixel(html, pixelUrl) {
  const img = `<img src="${pixelUrl}" width="1" height="1" alt="" style="border:0">`;
  const source = String(html || '');
  const bodyClose = source.search(/<\/body>(?![\s\S]*<\/body>)/i);
  return bodyClose >= 0
    ? `${source.slice(0, bodyClose)}${img}${source.slice(bodyClose)}`
    : `${source}${img}`;
}

export function htmlToPlainText(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('style, script, head').forEach((el) => el.remove());
  doc.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach((el) => el.append('\n'));
  return (doc.body?.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

export function buildGmailComposeUrl({ to, subject }) {
  const params = [
    'view=cm',
    'fs=1',
    `to=${encodeURIComponent(to || '')}`,
    `su=${encodeURIComponent(subject || '')}`,
    `body=${encodeURIComponent(GMAIL_PASTE_HINT)}`,
  ];
  return `https://mail.google.com/mail/?${params.join('&')}`;
}

/**
 * Starts the clipboard write synchronously (must be called directly inside the
 * click handler, before any await, to keep the user-activation).
 * Resolves to 'html' or 'text'; rejects when nothing could be copied.
 */
export function writeEmailHtmlToClipboard(html) {
  const plain = htmlToPlainText(html);
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : null;

  if (clip?.write && typeof window.ClipboardItem === 'function') {
    try {
      const item = new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      });
      return clip.write([item]).then(
        () => 'html',
        (err) => (clip.writeText ? clip.writeText(plain).then(() => 'text') : Promise.reject(err)),
      );
    } catch (err) {
      if (!clip.writeText) return Promise.reject(err);
    }
  }
  if (clip?.writeText) return clip.writeText(plain).then(() => 'text');
  return Promise.reject(new Error('Clipboard access is not available in this browser.'));
}

/** Synchronous rich-text copy via a hidden selection — fallback for the "Copy HTML" button. */
export function copyHtmlViaSelection(html) {
  const host = document.createElement('div');
  host.setAttribute('contenteditable', 'true');
  host.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none;';
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('style, script').forEach((el) => el.remove());
  host.innerHTML = doc.body?.innerHTML || '';
  document.body.appendChild(host);
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(host);
  selection.removeAllRanges();
  selection.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  selection.removeAllRanges();
  host.remove();
  return ok;
}
